import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import { RoleTypes } from "../auth/types/roleTypes";
import { UserTypes } from "../auth/types/authType";
import Toast from "../../components/Layout/Toast";
import { fetchWallPosts, selectWall } from "./wallSlices";
import { fetchOfficeUsers } from "./wallSearch";
import PostComposer from "./components/PostComposer";
import WallFilters, { WallFilterValues } from "./components/WallFilters";
import WallPostCard from "./components/WallPostCard";
import { WallPost, WallUser } from "./types/wallTypes";

const PAGE_SIZE = 20;

export default function Wall() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { posts, totalCount, error, success } = useAppSelector(selectWall);

  const [officeUsers, setOfficeUsers] = useState<WallUser[]>([]);
  const [filters, setFilters] = useState<WallFilterValues>({});
  const [editingPost, setEditingPost] = useState<WallPost | null>(null);
  const skipRef = useRef(0);

  const roleNames = useMemo(
    () => (user?.roles ?? []).map((r) => r.displayName),
    [user],
  );
  const isSuper = user?.userType === UserTypes.Super;
  const canPost = isSuper || roleNames.includes(RoleTypes.PostWall);
  const canModerate = isSuper || roleNames.includes(RoleTypes.ModerateWall);

  useEffect(() => {
    fetchOfficeUsers().then(setOfficeUsers);
  }, []);

  const load = (override?: WallFilterValues) => {
    skipRef.current = 0;
    dispatch(
      fetchWallPosts({
        skip: 0,
        take: PAGE_SIZE,
        ...(override ?? filters),
      }),
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = () => {
    skipRef.current += PAGE_SIZE;
    dispatch(
      fetchWallPosts({
        skip: skipRef.current,
        take: PAGE_SIZE,
        ...filters,
        append: true,
      }),
    );
  };

  const handleFilterChange = (values: WallFilterValues) => {
    setFilters(values);
    load(values);
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("wall")}
      </Typography>

      {canPost && (
        <PostComposer
          officeUsers={officeUsers}
          editingPost={editingPost}
          onDone={() => setEditingPost(null)}
        />
      )}

      <WallFilters officeUsers={officeUsers} onChange={handleFilterChange} />

      {posts.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
          {t("noPostsYet")}
        </Typography>
      ) : (
        posts.map((post) => (
          <WallPostCard
            key={post._id}
            post={post}
            currentUserId={user?._id}
            canModerate={canModerate}
            onEdit={setEditingPost}
          />
        ))
      )}

      {posts.length < totalCount && (
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Button onClick={loadMore}>{t("loadMore")}</Button>
        </Box>
      )}

      {error && <Toast severity="error">{error.message}</Toast>}
      {success && <Toast severity="success">{success.message}</Toast>}
    </Container>
  );
}
